import React from 'react';
import { User, Clock, Star } from 'lucide-react';
import { getCategory, getImageForChallenge, categoryStyles } from '../../utils/homeHelpers';

/**
 * Org-side Action card — the exact visual design used on WE (Home) and
 * now on the "All Actions" page. Extracted verbatim from MADSection so
 * both surfaces stay pixel-identical.
 *
 * Props:
 *   challenge   the challenge/action data object
 *   isJoined    boolean — join state
 *   isExpired   boolean — end_date has passed
 *   onJoin()    callback for the Join button (no-op if joined/expired)
 *   onOpen()    callback for the card body (usually navigate to detail)
 *   isHRAdmin   boolean — gates the "Featured" star badge visibility
 *   style       optional style overrides on the outer card (for use in
 *               horizontal-scroll shelves that need a min-width, etc.)
 */
const ActionCard = ({ challenge, isJoined, isExpired, onJoin, onOpen, isHRAdmin, style }) => {
  if (!challenge) return null;

  const category = getCategory(challenge.title, challenge.category);
  const catStyle = categoryStyles[category] || categoryStyles['Wellness'];

  return (
    <div
      onClick={onOpen}
      data-testid={`action-card-${challenge.id}`}
      style={{ background: '#FFFFFF', borderRadius: 12, overflow: 'hidden', cursor: 'pointer', border: '1px solid #E5E7EB', userSelect: 'none', ...style }}
    >
      {/* Cover Image */}
      <div style={{
        width: '100%', height: 140,
        background: `url(${getImageForChallenge(challenge)})`,
        backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative'
      }}>
        <div style={{
          position: 'absolute', top: 10, right: 10,
          padding: '4px 10px',
          background: catStyle.bg,
          color: catStyle.color,
          borderRadius: 12, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px'
        }}>
          {category}
        </div>
        {isHRAdmin && challenge.is_featured && (
          <div
            data-testid="mad-featured-badge"
            style={{
              position: 'absolute', top: 10, left: 10,
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 8px', borderRadius: 12,
              background: '#FFF8E1', color: '#78350F',
              border: '1px solid #F59E0B',
              fontSize: 10, fontWeight: 700, letterSpacing: '0.2px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}
          >
            <span aria-hidden style={{ fontSize: 11, lineHeight: 1 }}>★</span>
            <span>Featured</span>
          </div>
        )}
      </div>

      {/* Card Content */}
      <div style={{ padding: '12px 14px' }}>
        <h4 data-testid="mad-card-title" style={{
          fontSize: 14, fontWeight: 500, color: '#1F2937', marginBottom: 4, lineHeight: 1.4,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
        }}>
          {challenge.title}
        </h4>
        <p style={{
          fontSize: 13, color: '#6B7280', marginBottom: 12, lineHeight: 1.5,
          display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden'
        }}>
          {challenge.description}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, borderTop: '1px solid #F1F5F9' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 12, color: '#9CA3AF' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><User size={13} />{challenge.participants?.length || 0}</span>
            {(() => {
              // Frequency label. Prefer the real recurring_schedule
              // the create form actually writes; fall back to a
              // duration/challenge_type-derived label. `frequency`
              // is declared on the model but never populated by
              // any create path, so we can't rely on it — the
              // old `|| 'Weekly'` fallback was the reason every
              // card lied.
              const c = challenge || {};
              const recur = c.recurring_schedule;
              let label;
              if (recur && recur !== 'Does not repeat') {
                label = recur;
              } else if (c.challenge_type === 'specific-time' || (typeof c.duration_days === 'number' && c.duration_days <= 1)) {
                label = 'One-time';
              } else if (typeof c.duration_days === 'number' && c.duration_days > 1) {
                label = `${c.duration_days}-day`;
              } else {
                label = 'Ongoing';
              }
              return (
                <span data-testid="mad-card-frequency" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Clock size={13} />{label}
                </span>
              );
            })()}
            {(() => {
              // Stars per log for this action's mode. Mirrors the
              // real award formula in challenges.py:619-633:
              // base=10, multipliers {individual:1, team:3,
              // company:3, community:5}. No `|| 30` fallback —
              // org challenges never carry reward_points, so
              // that fallback was the reason every card showed 30.
              const modeMultiplier = { individual: 1, team: 3, company: 3, community: 5 };
              const perLogStars = 10 * (modeMultiplier[challenge?.mode] || 1);
              return (
                <span data-testid="mad-card-stars" style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#F59E0B' }}>
                  <Star size={13} fill="#F59E0B" />{perLogStars}
                </span>
              );
            })()}
            {challenge?.month && (
              <span style={{ padding: '2px 7px', background: '#EBF5FF', color: '#2563EB', fontSize: 10, fontWeight: 700, borderRadius: 8, border: '1px solid #BFDBFE', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {challenge.month}
              </span>
            )}
          </div>
          <button data-testid="mad-join-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (!isJoined && !isExpired && onJoin) onJoin(challenge.id);
            }}
            disabled={isExpired}
            style={{
              padding: '6px 16px',
              background: isExpired ? '#F1F5F9' : isJoined ? '#F1F5F9' : '#FFFFFF',
              color: isExpired ? '#94A3B8' : isJoined ? '#94A3B8' : '#374151',
              border: `1px solid ${isExpired ? '#E2E8F0' : isJoined ? '#E2E8F0' : '#D1D5DB'}`,
              borderRadius: 6, fontSize: 13, fontWeight: 500,
              cursor: isExpired || isJoined ? 'default' : 'pointer',
              transition: 'all 0.15s ease'
            }}>
            {isExpired ? 'Ended' : isJoined ? 'Joined' : 'Join'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActionCard;
